import React, { useCallback, useMemo, useState } from "react";
import PropTypes from "prop-types";
import UserCardAvatar from "../UserCardAvatar";
import MessageActionsPanel from "../MessageActionsPanel";
import MessageReactions from "../MessageReactions";
import "./index.scss";
import MessageThreadBar from "../MessageThreadBar";
import MessageStatus from "../../atoms/MessageStatus";
import useLocaleDateFormatter from "../../../hooks/useLocaleDateFormatter";

export default function MessageRow({
  messageId,
  activeMessageId,
  setActiveMessageId,
  userId,
  username,
  avatarUrl,
  timestamp,
  edited,
  status,
  children,
  onReact,
  onMore,
  quickEmojis,
  reactions,
  canReact,
  isEditing,
  threadReplyCount,
  onOpenThread,
}) {
  const displayName = username || "";
  const { formatTime } = useLocaleDateFormatter();

  const timestampDate = useMemo(() => {
    if (!timestamp) {
      return null;
    }
    const parsed = new Date(timestamp);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [timestamp]);

  const renderedTime = timestampDate ? formatTime(timestampDate) : "";
  const timeDateTimeValue = timestampDate ? timestampDate.toISOString() : undefined;

  const handleQuickReact = useCallback((emoji) => onReact?.(emoji, { remove: false, source: "quick" }), [onReact]);
  const handleMore = useCallback((event) => onMore?.(event), [onMore]);
  const handleToggleReaction = useCallback(
    (emoji, reacted) => onReact?.(emoji, { remove: reacted, source: "chip" }),
    [onReact]
  );
  const ariaLabel = displayName ? `${displayName} message` : "Message";

  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const isActive = !isEditing && (isEmojiPickerOpen || activeMessageId === messageId);
  const hasReactions = Array.isArray(reactions) && reactions.length > 0;

  return (
    <div
      className="message-row"
      role="listitem"
      tabIndex={0}
      aria-label={ariaLabel}
      onMouseEnter={() => setActiveMessageId?.(messageId)}
      onFocus={() => setActiveMessageId?.(messageId)}
      onMouseLeave={() => { if (!isEmojiPickerOpen) setActiveMessageId?.(null); }}
      onBlur={() => { if (!isEmojiPickerOpen) setActiveMessageId?.(null); }}
    >
      <UserCardAvatar
        userId={userId}
        username={displayName || undefined}
        avatarUrl={avatarUrl}
        alt={displayName || "User avatar"}
        className="message-row__avatar"
        size="44px"
        radius="25%"
      />
      <div className="message-row__body">
        <div className="message-row__header">
          <strong className="message-row__username">{displayName || "Unknown User"}</strong>
          <time className="message-row__time" dateTime={timeDateTimeValue}>{renderedTime}</time>
          {edited ? <span className="message-row__edited">(edited)</span> : null}
          {status ? (
            <span className="message-row__status">
              <MessageStatus status={status} />
            </span>
          ) : null}
        </div>
        <div className={`message-row__content${isEditing ? " message-row__content--editing" : ""}`}>{children}</div>
        {!isEditing && Number.isFinite(threadReplyCount) && threadReplyCount > 0 && (
          <MessageThreadBar
            count={threadReplyCount}
            onOpen={onOpenThread}
          />
        )}
        {!isEditing && hasReactions && (
          <MessageReactions
            reactions={reactions}
            onToggleReaction={handleToggleReaction}
            canReact={canReact}
          />
        )}
      </div>
      {isActive && (
        <MessageActionsPanel
          quickEmojis={quickEmojis}
          onQuickReact={handleQuickReact}
          onEmojiPickerOpenChange={(open) => {
            setIsEmojiPickerOpen(Boolean(open));
            if (open) {
              setActiveMessageId?.(messageId);
            }
          }}
          onMore={handleMore}
        />
      )}
    </div>
  );
}

MessageRow.propTypes = {
  userId: PropTypes.string,
  username: PropTypes.string,
  avatarUrl: PropTypes.string,
  timestamp: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  edited: PropTypes.bool,
  status: PropTypes.string,
  children: PropTypes.node,
  onReact: PropTypes.func,
  onMore: PropTypes.func,
  quickEmojis: PropTypes.arrayOf(PropTypes.string),
  reactions: PropTypes.arrayOf(PropTypes.shape({
    emoji: PropTypes.string.isRequired,
    count: PropTypes.number,
    reacted: PropTypes.bool,
  })),
  canReact: PropTypes.bool,
  isEditing: PropTypes.bool,
  threadReplyCount: PropTypes.number,
  onOpenThread: PropTypes.func,
};

MessageRow.defaultProps = {
  userId: undefined,
  username: "",
  avatarUrl: undefined,
  timestamp: undefined,
  edited: false,
  status: null,
  children: null,
  onReact: undefined,
  onMore: undefined,
  quickEmojis: ["✅", "👀", "🙌"],
  reactions: [],
  canReact: true,
  isEditing: false,
  threadReplyCount: 0,
  onOpenThread: undefined,
};
